import { defineComponent, shallowRef, computed, ref, watch, openBlock, createElementBlock, mergeProps, createElementVNode, normalizeClass, withKeys, Fragment, renderList, renderSlot, normalizeProps, createCommentVNode, guardReactiveProps, createVNode, unref, createBlock } from "vue";
import { _ as _sfc_main$2 } from "./VaStepperControls.vue_vue_type_script_setup_true_lang.js";
import { _ as _sfc_main$1 } from "./VaStepperStepButton.vue_vue_type_script_setup_true_lang.js";
import { i as isStepHasError } from "./step.js";
import { u as useStatefulProps, b as useStateful } from "../../composables/useStateful.js";
import { u as useColors } from "../../composables/useColors.js";
import { u as useTranslation } from "../../composables/useTranslation.js";
const _hoisted_1 = { class: "va-stepper__step-content" };
const _hoisted_2 = { class: "va-stepper__controls" };
const _sfc_main = /* @__PURE__ */ defineComponent({
  ...{
    name: "VaStepper"
  },
  __name: "VaStepper",
  props: {
    ...useStatefulProps,
    modelValue: { type: Number, default: 0 },
    steps: {
      type: Array,
      default: () => [],
      required: true
    },
    color: { type: String, default: "primary" },
    vertical: { type: Boolean, default: false },
    navigationDisabled: { type: Boolean, default: false },
    controlsHidden: { type: Boolean, default: false },
    nextDisabled: { type: Boolean, default: false },
    nextDisabledOnError: { type: Boolean, default: false },
    finishButtonHidden: { type: Boolean, default: false },
    ariaLabel: { type: String, default: "$t:progress" },
    linear: { type: Boolean, default: false },
    /** Hidden step shown when all steps complete */
    finishStep: { type: Object }
  },
  emits: ["update:modelValue", "finish", "update:steps"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const stepperNavigation = shallowRef();
    const { valueComputed: modelValue } = useStateful(props, emit, "modelValue");
    const stepsComputed = computed(() => {
      if (!props.finishStep) {
        return props.steps;
      }
      return [...props.steps, props.finishStep];
    });
    const isFinishStep = (index) => {
      if (!props.finishStep) {
        return false;
      }
      return index === stepsComputed.value.length - 1;
    };
    const focusedStep = ref({ trigger: false, stepIndex: props.navigationDisabled ? -1 : props.modelValue });
    const { getColor } = useColors();
    const isNextStepDisabled = (index) => {
      if (props.nextDisabledOnError && isStepHasError(stepsComputed.value[index])) {
        return true;
      }
      return props.nextDisabled;
    };
    const findFirstNonDisabled = (from, direction) => {
      while (from >= 0 && from < stepsComputed.value.length) {
        from += direction;
        const step = stepsComputed.value[from];
        if (!step) {
          return;
        }
        if (!step.disabled) {
          return step;
        }
      }
    };
    const findFirstWithErrorIndex = (from, direction) => {
      while (from >= 0 && from < stepsComputed.value.length) {
        from += direction;
        const step = stepsComputed.value[from];
        if (!step) {
          return;
        }
        if (isStepHasError(step) === true) {
          return from;
        }
      }
    };
    const validateMovingToStep = async (stepIndex) => {
      var _a;
      const newStep = stepsComputed.value[stepIndex];
      const currentStep = stepsComputed.value[modelValue.value];
      const beforeNewStep = findFirstNonDisabled(stepIndex, -1);
      if (newStep.disabled) {
        return false;
      }
      if (props.linear && stepIndex < modelValue.value) {
        return true;
      }
      const nextNonError = findFirstWithErrorIndex(modelValue.value, 1);
      if (props.linear && nextNonError !== void 0 && nextNonError < stepIndex) {
        return false;
      }
      let currentStepBeforeLeaveResult;
      try {
        currentStepBeforeLeaveResult = await ((_a = currentStep.beforeLeave) == null ? void 0 : _a.call(currentStep, currentStep, newStep));
      } catch (e) {
        throw new Error(`Error in beforeLeave function: ${e}`);
      }
      if (currentStepBeforeLeaveResult === false) {
        return false;
      }
      if (currentStep.completed === void 0) {
        currentStep.completed = true;
      }
      if (props.linear && beforeNewStep && !beforeNewStep.completed) {
        return false;
      }
      if (props.linear && isStepHasError(currentStep)) {
        return false;
      }
      return true;
    };
    const setStep = async (index) => {
      if (!await validateMovingToStep(index)) {
        return;
      }
      modelValue.value = index;
    };
    const setFocus = (direction) => {
      if (props.navigationDisabled) {
        return;
      }
      if (direction === "next") {
        setFocusNextStep(1);
      } else {
        setFocusPrevStep(1);
      }
    };
    const setFocusNextStep = (idx = 1) => {
      const newValue = focusedStep.value.stepIndex + idx;
      if (isNextStepDisabled(newValue)) {
        return;
      }
      if (newValue < stepsComputed.value.length) {
        if (stepsComputed.value[newValue].disabled) {
          setFocusNextStep(idx + 1);
          return;
        }
        focusedStep.value.stepIndex = newValue;
        focusedStep.value.trigger = true;
      } else {
        for (let availableIdx = 0; availableIdx < stepsComputed.value.length; availableIdx++) {
          if (!stepsComputed.value[availableIdx].disabled) {
            focusedStep.value.stepIndex = availableIdx;
            focusedStep.value.trigger = true;
            break;
          }
        }
      }
    };
    const setFocusPrevStep = (idx = 1) => {
      const newValue = focusedStep.value.stepIndex - idx;
      if (newValue >= 0) {
        if (stepsComputed.value[newValue].disabled) {
          setFocusPrevStep(idx + 1);
          return;
        }
        focusedStep.value.stepIndex = newValue;
        focusedStep.value.trigger = true;
      } else {
        for (let availableIdx = stepsComputed.value.length - 1; availableIdx >= 0; availableIdx--) {
          if (!stepsComputed.value[availableIdx].disabled && !isNextStepDisabled(availableIdx)) {
            focusedStep.value.stepIndex = availableIdx;
            focusedStep.value.trigger = true;
            break;
          }
        }
      }
    };
    const resetFocus = () => {
      requestAnimationFrame(() => {
        var _a;
        if (!((_a = stepperNavigation.value) == null ? void 0 : _a.contains(document.activeElement))) {
          focusedStep.value.stepIndex = props.modelValue;
          focusedStep.value.trigger = false;
        }
      });
    };
    watch(() => props.modelValue, () => {
      focusedStep.value.stepIndex = props.modelValue;
      focusedStep.value.trigger = false;
    });
    const nextStep = (stepsToSkip = 0) => {
      const targetIndex = modelValue.value + 1 + stepsToSkip;
      if (!stepsComputed.value[targetIndex]) {
        return;
      }
      if (stepsComputed.value[targetIndex].disabled) {
        nextStep(stepsToSkip + 1);
      }
      setStep(targetIndex);
    };
    const prevStep = (stepsToSkip = 0) => {
      const targetIndex = modelValue.value - 1 - stepsToSkip;
      if (!stepsComputed.value[targetIndex]) {
        return;
      }
      if (stepsComputed.value[targetIndex].disabled) {
        prevStep(stepsToSkip + 1);
      }
      setStep(targetIndex);
    };
    const finish = async () => {
      if (await validateMovingToStep(props.steps.length - 1)) {
        emit("finish");
      }
    };
    const stepControls = { setStep, nextStep, prevStep, finish };
    const getIterableSlotData = (step, index) => ({
      ...stepControls,
      focus: focusedStep,
      isActive: props.modelValue === index,
      isCompleted: props.modelValue > index,
      isLastStep: stepsComputed.value.length - 1 === index,
      isNextStepDisabled: isNextStepDisabled(index),
      isPrevStepDisabled: index === 0,
      index,
      step,
      hasError: isStepHasError(step)
    });
    const { tp } = useTranslation();
    const onValueChange = () => {
      focusedStep.value.stepIndex = props.modelValue;
      focusedStep.value.trigger = true;
    };
    const ariaAttributesComputed = computed(() => ({
      role: "group",
      "aria-label": tp(props.ariaLabel),
      "aria-orientation": props.vertical ? "vertical" : "horizontal"
    }));
    function getStepperButtonColor(index) {
      return isStepHasError(stepsComputed.value[index]) ? "danger" : getColor(props.color);
    }
    const completeStep = (shouldCompleteStep) => {
      const steps = { ...stepsComputed.value };
      if (shouldCompleteStep === true) {
        steps[props.modelValue].hasError = false;
      }
      steps[props.modelValue].completed = shouldCompleteStep ?? true;
      emit("update:steps", steps);
    };
    const setError = (shouldSetError) => {
      const steps = { ...stepsComputed.value };
      steps[props.modelValue].hasError = shouldSetError ?? true;
      steps[props.modelValue].completed = !shouldSetError;
      emit("update:steps", steps);
    };
    __expose({
      modelValue,
      focusedStep,
      getIterableSlotData,
      stepControls,
      nextStep,
      prevStep,
      setStep,
      setFocus,
      completeStep,
      setError
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", mergeProps({
        class: ["va-stepper", { "va-stepper--vertical": _ctx.$props.vertical }]
      }, ariaAttributesComputed.value), [
        createElementVNode("ol", {
          class: normalizeClass(["va-stepper__navigation", { "va-stepper__navigation--vertical": _ctx.$props.vertical }]),
          ref_key: "stepperNavigation",
          ref: stepperNavigation,
          onClick: onValueChange,
          onKeyup: [
            withKeys(onValueChange, ["enter"]),
            withKeys(onValueChange, ["space"]),
            _cache[0] || (_cache[0] = withKeys(($event) => setFocus("prev"), ["left"])),
            _cache[1] || (_cache[1] = withKeys(($event) => setFocus("next"), ["right"]))
          ],
          onFocusout: resetFocus
        }, [
          (openBlock(true), createElementBlock(Fragment, null, renderList(stepsComputed.value, (step, i) => {
            return openBlock(), createElementBlock(Fragment, {
              key: i + step.label
            }, [
              !isFinishStep(i) ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
                i > 0 ? renderSlot(_ctx.$slots, "divider", normalizeProps(mergeProps({ key: 0 }, getIterableSlotData(step, i))), () => [
                  createElementVNode("span", {
                    class: normalizeClass(["va-stepper__divider", { "va-stepper__divider--vertical": _ctx.$props.vertical }]),
                    "aria-hidden": "true"
                  }, null, 2)
                ]) : createCommentVNode("", true),
                renderSlot(_ctx.$slots, `step-button-${i}`, normalizeProps(guardReactiveProps(getIterableSlotData(step, i))), () => [
                  createVNode(_sfc_main$1, {
                    stepIndex: i,
                    color: getStepperButtonColor(i),
                    modelValue: unref(modelValue),
                    nextDisabled: __props.nextDisabled,
                    step,
                    stepControls,
                    navigationDisabled: __props.navigationDisabled,
                    focus: focusedStep.value
                  }, null, 8, ["stepIndex", "color", "modelValue", "nextDisabled", "step", "navigationDisabled", "focus"])
                ])
              ], 64)) : createCommentVNode("", true)
            ], 64);
          }), 128))
        ], 34),
        createElementVNode("div", {
          class: normalizeClass(["va-stepper__step-content-wrapper", { "va-stepper__step-content-wrapper--vertical": _ctx.$props.vertical }])
        }, [
          createElementVNode("div", _hoisted_1, [
            renderSlot(_ctx.$slots, `step-content-${isFinishStep(unref(modelValue)) ? "finish" : unref(modelValue)}`, normalizeProps(guardReactiveProps(getIterableSlotData(stepsComputed.value[unref(modelValue)], unref(modelValue)))))
          ]),
          createElementVNode("div", _hoisted_2, [
            renderSlot(_ctx.$slots, "controls", normalizeProps(guardReactiveProps(getIterableSlotData(stepsComputed.value[unref(modelValue)], unref(modelValue)))), () => [
              !__props.controlsHidden ? (openBlock(), createBlock(_sfc_main$2, {
                key: 0,
                modelValue: unref(modelValue),
                nextDisabled: isNextStepDisabled(unref(modelValue)),
                steps: stepsComputed.value,
                stepControls,
                finishButtonHidden: __props.finishButtonHidden
              }, null, 8, ["modelValue", "nextDisabled", "steps", "finishButtonHidden"])) : createCommentVNode("", true)
            ])
          ])
        ], 2)
      ], 16);
    };
  }
});
export {
  _sfc_main as _
};

import '../../../VaStepper.css';
//# sourceMappingURL=VaStepper.vue_vue_type_script_setup_true_lang.js.map
